import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../../contexts/AuthContext';
import { XMarkIcon, CreditCardIcon, BanknotesIcon } from '@heroicons/react/24/outline';
import { payrollService } from '../../../services/accounting/payroll';
import { formatCurrency } from '../../../utils/formatting';
import toast from 'react-hot-toast';

/**
 * PayrollPaymentModal Component
 * 
 * Modal for processing payroll payments
 */
const PayrollPaymentModal = ({ isOpen, onClose, onSuccess, entryIds, entries }) => {
  const { t } = useTranslation();
  const { currentCompany, currentUser } = useAuth();
  
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    paymentDate: new Date().toISOString().split('T')[0],
    paymentMethod: 'bank',
    paymentReference: ''
  });

  const totalAmount = entries?.reduce((sum, entry) => sum + (entry.totalDue || 0), 0) || 0;

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!currentCompany?.id || !entryIds?.length) return;
    
    try {
      setLoading(true);
      
      await payrollService.processPayrollPayment(
        currentCompany.id,
        entryIds,
        formData,
        currentUser?.uid || 'system'
      );
      
      toast.success(t('payroll.success.payment_processed', 'Payment processed successfully'));
      onSuccess();
      
    } catch (error) {
      console.error('Error processing payment:', error);
      toast.error(t('payroll.error.payment_failed', 'Failed to process payment'));
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setFormData({
      paymentDate: new Date().toISOString().split('T')[0],
      paymentMethod: 'bank',
      paymentReference: ''
    });
    onClose();
  };

  if (!isOpen || !entries?.length) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl mx-4 max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">
            {t('payroll.process_payment', 'Process Payment')}
          </h2>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
            disabled={loading}
          >
            <XMarkIcon className="w-6 h-6" />
          </button>
        </div>

        <div className="overflow-y-auto max-h-[calc(90vh-140px)]">
          {/* Payment Summary */}
          <div className="p-6 space-y-6">
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <h3 className="text-lg font-medium text-green-900 mb-3">
                {t('payroll.payment_summary', 'Payment Summary')}
              </h3>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div className="flex justify-between">
                  <span>{t('payroll.employees_to_pay', 'Employees to Pay')}:</span>
                  <span className="font-medium">{entries.length}</span>
                </div>
                <div className="flex justify-between">
                  <span>{t('payroll.total_amount', 'Total Amount')}:</span>
                  <span className="font-medium text-lg">{formatCurrency(totalAmount)}</span>
                </div>
              </div>
            </div>

            {/* Employee List */}
            <div>
              <h4 className="font-medium text-gray-900 mb-3">
                {t('payroll.employees_list', 'Employees List')}
              </h4>
              <div className="overflow-x-auto">
                <table className="table">
                  <thead>
                    <tr>
                      <th>{t('payroll.employee_name', 'Employee Name')}</th>
                      <th>{t('payroll.role', 'Role')}</th>
                      <th>{t('payroll.salary', 'Salary')}</th>
                      <th>{t('payroll.commission', 'Commission')}</th>
                      <th>{t('payroll.total_due', 'Total Due')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {entries.map(entry => (
                      <tr key={entry.id}>
                        <td className="font-medium">{entry.employeeName}</td>
                        <td>{entry.employeeRole}</td>
                        <td>{formatCurrency(entry.salary || 0)}</td>
                        <td>{formatCurrency(entry.commission || 0)}</td>
                        <td className="font-medium">{formatCurrency(entry.totalDue || 0)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Payment Form */}
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('payroll.payment_date', 'Payment Date')}
                  </label>
                  <input
                    type="date"
                    value={formData.paymentDate}
                    onChange={(e) => setFormData({ ...formData, paymentDate: e.target.value })}
                    className="form-input"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('payroll.payment_method', 'Payment Method')}
                  </label>
                  <select
                    value={formData.paymentMethod}
                    onChange={(e) => setFormData({ ...formData, paymentMethod: e.target.value })}
                    className="form-select"
                    required
                  >
                    <option value="bank">{t('payroll.bank_transfer', 'Bank Transfer')}</option>
                    <option value="cash">{t('payroll.cash', 'Cash')}</option>
                    <option value="check">{t('payroll.check', 'Check')}</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('payroll.payment_reference', 'Payment Reference')}
                </label>
                <input
                  type="text"
                  value={formData.paymentReference}
                  onChange={(e) => setFormData({ ...formData, paymentReference: e.target.value })}
                  className="form-input"
                  placeholder={t('payroll.payment_reference_placeholder', 'Transaction ID, Check number, etc.')}
                  required
                />
              </div>

              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <div className="flex items-start">
                  <div className="flex-shrink-0">
                    {formData.paymentMethod === 'cash' ? (
                      <BanknotesIcon className="w-5 h-5 text-yellow-600" />
                    ) : (
                      <CreditCardIcon className="w-5 h-5 text-yellow-600" />
                    )}
                  </div>
                  <div className="ml-3">
                    <h4 className="text-sm font-medium text-yellow-900">
                      {t('payroll.payment_confirmation', 'Payment Confirmation')}
                    </h4>
                    <p className="text-sm text-yellow-700 mt-1">
                      {t('payroll.payment_warning', 'This will mark all selected payroll entries as paid and create accounting journal entries. This action cannot be undone.')}
                    </p>
                  </div>
                </div>
              </div>
            </form>
          </div>
        </div>

        {/* Actions */}
        <div className="flex items-center justify-end space-x-3 p-6 border-t border-gray-200">
          <button
            onClick={handleClose}
            className="btn btn-secondary"
            disabled={loading}
          >
            {t('common.cancel', t('common.cancel'))}
          </button>
          <button
            onClick={handleSubmit}
            className="btn btn-success"
            disabled={loading || !formData.paymentReference.trim()}
          >
            {loading ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                {t('common.processing', 'Processing...')}
              </>
            ) : (
              <>
                <CreditCardIcon className="w-4 h-4 mr-2" />
                {t('payroll.process_payment', 'Process Payment')} ({formatCurrency(totalAmount)})
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default PayrollPaymentModal;